import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Button, Modal, Form, Table, Badge, Spinner, Dropdown } from 'react-bootstrap';
import { Edit, Trash, Calendar, Add, More } from 'iconsax-react';
import MyApiClient from '../../../../api-client/MyApiClient';
import Select from 'react-select';
import { sports } from '../../Util/enum';
import chiediConferma from '../../Util/chiediConferma';

const DisponibilitaMaestro = ({ profile, readonly = false }) => {
    const [disponibilita, setDisponibilita] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [modalTitle, setModalTitle] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [showActivateModal, setShowActivateModal] = useState(false);
    const [itemToActivate, setItemToActivate] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [itemToCancel, setItemToCancel] = useState(null);
    const [enabledSports, setEnabledSports] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    
    const [formData, setFormData] = useState({
        sport: '',
        startDate: '',
        endDate: '',
        scheduleItems: []
    });

    const dayOptions = [
        { value: 'MONDAY', label: 'Lunedì' },
        { value: 'TUESDAY', label: 'Martedì' },
        { value: 'WEDNESDAY', label: 'Mercoledì' },
        { value: 'THURSDAY', label: 'Giovedì' },
        { value: 'FRIDAY', label: 'Venerdì' },
        { value: 'SATURDAY', label: 'Sabato' },
        { value: 'SUNDAY', label: 'Domenica' }
    ];

    const statusLabels = {
        'DRAFT': { label: 'Bozza', class: 'warning' },
        'CANCELLED': { label: 'Annullato', class: 'danger' },
        'ACTIVE': { label: 'Attiva', class: 'success' }
    };

    const fetchDisponibilita = useCallback(async () => {
        try {
            setLoading(true);
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                const token = tokenDetails.idToken;
                const apiClient = new MyApiClient();
                
                // Chiamata API per ottenere le disponibilità del guru
                const response = await apiClient.getGuruSchedules(token, profile.idsGuru.userId);
                
                if (response.error) {
                    console.error('Errore API:', response.error);
                    setDisponibilita([]);
                } else {
                    setDisponibilita(response || []);
                }
            }
        } catch (error) {
            console.error('Errore nel caricamento delle disponibilità:', error);
        } finally {
            setLoading(false);
        }
    }, [profile]);

    useEffect(() => {
        if (profile && profile.idsGuru && profile.idsGuru.userId) {
            fetchDisponibilita();
        }
    }, [profile, fetchDisponibilita]);

    useEffect(() => {
        const fetchEnabledSportsForGuru = async () => {
            try {
                const tokenDetailsString = sessionStorage.getItem('userDetails');
                if (tokenDetailsString && profile && profile.idsGuru && profile.idsGuru.userId) {
                    const tokenDetails = JSON.parse(tokenDetailsString);
                    const token = tokenDetails.idToken;
                    const apiClient = new MyApiClient();
                    const memberships = await apiClient.getMemberships(token, 'COACH', null, 'CONFIRMED');
                    const guruId = profile.idsGuru.userId;
                    const approvedSportsValues = Array.isArray(memberships)
                        ? memberships
                            .filter(m => m.userId === guruId && m.membershipRank === 'COACH' && m.status === 'CONFIRMED' && m.sport)
                            .map(m => m.sport)
                        : [];
                    const filtered = sports.filter(s => approvedSportsValues.includes(s.value));
                    setEnabledSports(filtered);
                }
            } catch (error) {
                console.error('Errore nel caricamento degli sport approvati:', error);
                setEnabledSports([]);
            }
        };
        fetchEnabledSportsForGuru();
    }, [profile]);

    const handleOpenModal = (item = null) => {
        setErrorMessage('');
        if (item) {
            setEditingItem(item);
            setModalTitle('Modifica Disponibilità');
            setFormData({
                sport: item.sport,
                startDate: item.startDate,
                endDate: item.endDate,
                scheduleItems: item.scheduleItems || []
            });
        } else {
            setEditingItem(null);
            setModalTitle('Nuova Disponibilità');
            setFormData({
                sport: '',
                startDate: '',
                endDate: '',
                scheduleItems: []
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingItem(null);
        setErrorMessage('');
        setFormData({
            sport: '',
            startDate: '',
            endDate: '',
            scheduleItems: []
        });
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                const token = tokenDetails.idToken;
                const apiClient = new MyApiClient();
                
                if (editingItem) {
                    // Aggiorna schedulazione esistente (solo se è una bozza)
                    if (editingItem.status === 'DRAFT') {
                        const scheduleData = {
                            status: 'DRAFT',
                            sport: formData.sport,
                            startDate: formData.startDate,
                            endDate: formData.endDate,
                            scheduleItems: formData.scheduleItems
                        };
                        
                        const response = await apiClient.updateGuruSchedule(token, editingItem.id, scheduleData);
                        
                        if (response.error) {
                            console.error('Errore API:', response.error);
                            setErrorMessage('Errore durante l\'aggiornamento della schedulazione: ' + response.error);
                            return;
                        } else {
                            // Ricarica i dati dopo l'aggiornamento
                            await fetchDisponibilita();
                        }
                    } else {
                        setErrorMessage('Puoi modificare solo le schedulazioni in stato DRAFT');
                        return;
                    }
                } else {
                    // Create new schedule
                    const scheduleData = {
                        guruId: profile.idsGuru.userId,
                        status: 'DRAFT',
                        sport: formData.sport,
                        startDate: formData.startDate,
                        endDate: formData.endDate,
                        scheduleItems: formData.scheduleItems
                    };
                    
                    const response = await apiClient.createGuruSchedule(token, scheduleData);
                    
                    if (response.error) {
                        console.error('Errore API:', response.error);
                        setErrorMessage('Errore durante la creazione della schedulazione: ' + response.error);
                        return;
                    } else {
                        // Ricarica i dati dopo la creazione
                        await fetchDisponibilita();
                    }
                }
            }
            
            handleCloseModal();
        } catch (error) {
            console.error('Errore nel salvataggio:', error);
            setErrorMessage('Errore durante il salvataggio: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                const token = tokenDetails.idToken;
                const apiClient = new MyApiClient();
                
                // Elimina schedulazione (solo se è una bozza)
                if (itemToDelete.status === 'DRAFT') {
                    const response = await apiClient.deleteGuruSchedule(token, itemToDelete.id);
                    
                    if (response.error) {
                        console.error('Errore API:', response.error);
                        alert('Errore durante l\'eliminazione della schedulazione: ' + response.error);
                    } else {
                        // Ricarica i dati dopo l'eliminazione
                        await fetchDisponibilita();
                    }
                } else {
                    alert('Puoi eliminare solo le schedulazioni in stato DRAFT');
                }
            }
            
            setShowDeleteModal(false);
            setItemToDelete(null);
        } catch (error) {
            console.error('Errore nell\'eliminazione:', error);
            alert('Errore durante l\'eliminazione');
        } finally {
            setLoading(false);
        }
    };

    const handleActivate = (item) => {
        setItemToActivate(item);
        setShowActivateModal(true);
    };

    const handleCancel = (item) => {
        setItemToCancel(item);
        setShowCancelModal(true);
    };

    const confirmActivate = async () => {
        try {
            setLoading(true);
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                const token = tokenDetails.idToken;
                const apiClient = new MyApiClient();
                
                const response = await apiClient.activateGuruSchedule(token, itemToActivate.id);
                
                if (response.error) {
                    console.error('Errore API:', response.error);
                    alert('Errore durante l\'attivazione della schedulazione: ' + response.error);
                } else {
                    // Ricarica i dati dopo l'attivazione
                    await fetchDisponibilita();
                }
            }
            setShowActivateModal(false);
            setItemToActivate(null);
        } catch (error) {
            console.error('Errore nell\'attivazione:', error);
            alert('Errore durante l\'attivazione');
        } finally {
            setLoading(false);
        }
    };

    const confirmCancel = async () => {
        try {
            setLoading(true);
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                const token = tokenDetails.idToken;
                const apiClient = new MyApiClient();
                
                const response = await apiClient.cancelGuruSchedule(token, itemToCancel.id);
                
                if (response.error) {
                    console.error('Errore API:', response.error);
                    alert('Errore durante la cancellazione della schedulazione: ' + response.error);
                } else {
                    // Ricarica i dati dopo la cancellazione
                    await fetchDisponibilita();
                }
            }
            setShowCancelModal(false);
            setItemToCancel(null);
        } catch (error) {
            console.error('Errore nella cancellazione:', error);
            alert('Errore durante la cancellazione');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAndActivate = async () => {
        try {
            setLoading(true);
            
            // Validazione dei campi obbligatori
            if (!formData.sport) {
                setErrorMessage('Per favore, seleziona uno sport.');
                return;
            }
            
            if (!formData.startDate || !formData.endDate) {
                setErrorMessage('Per favore, inserisci le date di inizio e fine.');
                return;
            }
            
            if (!formData.scheduleItems || formData.scheduleItems.length === 0) {
                setErrorMessage('Per favore, aggiungi almeno un orario settimanale.');
                return;
            }
            
            // Validazione degli orari
            for (let i = 0; i < formData.scheduleItems.length; i++) {
                const item = formData.scheduleItems[i];
                if (!item.dayOfWeek || !item.startTime || !item.endTime) {
                    setErrorMessage(`Per favore, completa tutti i campi per l'orario ${i + 1}.`);
                    return;
                }
            }
            
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (tokenDetailsString) {
                const tokenDetails = JSON.parse(tokenDetailsString);
                const token = tokenDetails.idToken;
                const apiClient = new MyApiClient();
                
                const scheduleData = {
                    guruId: profile.idsGuru.userId,
                    sport: formData.sport,
                    status: "DRAFT",
                    startDate: formData.startDate,
                    endDate: formData.endDate,
                    scheduleItems: formData.scheduleItems.map(item => ({
                        dayOfWeek: item.dayOfWeek,
                        startTime: item.startTime,
                        endTime: item.endTime,
                        location: item.location
                    }))
                };
                
                console.log('Sending scheduleData:', scheduleData);
                
                let saveResponse;
                let scheduleId;
                
                if (editingItem) {
                    saveResponse = await apiClient.updateGuruSchedule(token, editingItem.id, scheduleData);
                    scheduleId = editingItem.id;
                } else {
                    saveResponse = await apiClient.createGuruSchedule(token, scheduleData);
                    console.log('Response:', saveResponse);
                    scheduleId = saveResponse.id;
                }
                
                if (saveResponse.error) {
                    console.error('Errore API nel salvataggio:', saveResponse.error);
                    setErrorMessage('Errore durante il salvataggio: ' + saveResponse.error);
                    return;
                }
                
                // Se il salvataggio è andato a buon fine, procedi con l'attivazione
                if (scheduleId) {
                    const activateResponse = await apiClient.activateGuruSchedule(token, scheduleId);
                    
                    if (activateResponse.error) {
                        console.error('Errore API nell\'attivazione:', activateResponse.error);
                        setErrorMessage('Salvataggio completato, ma errore durante l\'attivazione: ' + activateResponse.error);
                    } else {
                        handleCloseModal();
                        await fetchDisponibilita();
                    }
                } else {
                    setErrorMessage('Errore: ID della schedulazione non trovato dopo il salvataggio');
                }
            }
        } catch (error) {
            console.error('Errore nel salvataggio e attivazione:', error);
            setErrorMessage('Errore durante il salvataggio e attivazione: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const addScheduleItem = () => {
        setFormData({
            ...formData,
            scheduleItems: [
                ...formData.scheduleItems,
                {
                    id: Date.now().toString(),
                    dayOfWeek: '',
                    startTime: '',
                    endTime: '',
                    location: ''
                }
            ]
        });
    };

    const generateAllDays = () => {
        const items = formData.scheduleItems || [];
        const uniqueDays = Array.from(new Set(items.map(i => i.dayOfWeek).filter(Boolean)));

        if (uniqueDays.length === 0) {
            const allDaysItems = dayOptions.map((day, index) => ({
                id: (Date.now() + index).toString(),
                dayOfWeek: day.value,
                startTime: '08:00',
                endTime: '20:00',
                location: ''
            }));
            setFormData({
                ...formData,
                scheduleItems: [
                    ...items,
                    ...allDaysItems
                ]
            });
            return;
        }

        if (uniqueDays.length > 1) {
            return;
        }

        const presentDay = uniqueDays[0];
        const baseItems = items.filter(i => i.dayOfWeek === presentDay && i.startTime && i.endTime);
        if (baseItems.length === 0) {
            return;
        }

        const daysToAdd = dayOptions.filter(d => d.value !== presentDay);
        const newItems = [];
        daysToAdd.forEach((day) => {
            baseItems.forEach((bi, idx) => {
                newItems.push({
                    id: (Date.now() + newItems.length + idx).toString(),
                    dayOfWeek: day.value,
                    startTime: bi.startTime,
                    endTime: bi.endTime,
                    location: bi.location || ''
                });
            });
        });

        setFormData({
            ...formData,
            scheduleItems: [
                ...items,
                ...newItems
            ]
        });
    };

    const updateScheduleItem = (index, field, value) => {
        const updatedItems = formData.scheduleItems.map((item, i) => 
            i === index ? { ...item, [field]: value } : item
        );
        setFormData({ ...formData, scheduleItems: updatedItems });
    };

    const removeScheduleItem = (index) => {
        const updatedItems = formData.scheduleItems.filter((_, i) => i !== index);
        setFormData({ ...formData, scheduleItems: updatedItems });
    };

    const canEdit = (item) => {
        return item.status !== 'CANCELLED';
    };

    const getDayLabel = (dayValue) => {
        const day = dayOptions.find(d => d.value === dayValue);
        return day ? day.label : dayValue;
    };

    const getSportLabel = (sportValue) => {
        const sport = enabledSports.find(s => s.value === sportValue) || sports.find(s => s.value === sportValue);
        return sport ? sport.label : sportValue;
    };

    if (loading && disponibilita.length === 0) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" role="status">
                    <span className="sr-only">Caricamento...</span>
                </Spinner>
                <div>Caricamento disponibilità...</div>
            </div>
        );
    }

    return (
        <>
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Gestione Disponibilità</h5>
                    {!readonly && (
                        <Button 
                            variant="primary" 
                            size="sm" 
                            className="btn-add"
                            onClick={() => handleOpenModal()}
                            disabled={loading}
                        >
                            <Add size="16" className="me-1" />
                            Nuova Disponibilità
                        </Button>
                    )}
                </Card.Header>
                <Card.Body>
                    {(() => {
                        const filteredDisponibilita = disponibilita.filter(item => item.status !== 'CANCELLED');
                        return filteredDisponibilita.length === 0 ? (
                            <div className="text-center">
                                <Calendar size="64" className="text-muted mb-4" />
                                <h6 className="text-muted mb-3">Nessuna disponibilità configurata</h6>
                                <p className="text-muted mb-4">Inizia creando la tua prima disponibilità per lo staff tecnico</p>
                                {!readonly && (
                                    <Button variant="primary" size="lg" style={{margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center'}} onClick={() => handleOpenModal()}>
                                        <Add size="20" className="me-2" />
                                        Aggiungi la prima disponibilità
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <Table responsive>
                                <thead>
                                    <tr>
                                        <th>Sport</th>
                                        <th>Periodo</th>
                                        <th>Stato</th>
                                        <th>Orari</th>
                                        {!readonly && <th>Azioni</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDisponibilita.map((item) => (
                                    <tr key={item.id}>
                                        <td>
                                            <strong>{getSportLabel(item.sport)}</strong>
                                        </td>
                                        <td>
                                            {new Date(item.startDate).toLocaleDateString('it-IT')} - {new Date(item.endDate).toLocaleDateString('it-IT')}
                                        </td>
                                        <td>
                                            <Badge bg={statusLabels[item.status]?.class || 'secondary'}>
                                                {statusLabels[item.status]?.label || item.status}
                                            </Badge>
                                        </td>
                                        <td>
                                            {item.scheduleItems && item.scheduleItems.length > 0 ? (
                                                <div>
                                                    {item.scheduleItems.map((schedule, idx) => (
                                                        <div key={schedule.id || idx} className="small">
                                                            <strong>{getDayLabel(schedule.dayOfWeek)}</strong>: {schedule.startTime} - {schedule.endTime}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-muted">Nessun orario configurato</span>
                                            )}
                                        </td>
                                        {!readonly && (
                                            <td>
                                                {(item.status === 'DRAFT' || item.status === 'ACTIVE') && (
                                                    <Dropdown drop="auto">
                                                        <Dropdown.Toggle className="btn-primary light sharp i-false btn-action">
                                                            <More />
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu>
                                                            {item.status === 'DRAFT' && (
                                                                <>
                                                                    <Dropdown.Item 
                                                                        onClick={() => handleOpenModal(item)}
                                                                        disabled={loading}
                                                                    >
                                                                        <span className="me-2"><Edit size="20" /></span> Modifica
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Item 
                                                                        onClick={() => handleActivate(item)}
                                                                        disabled={loading}
                                                                    >
                                                                        <span className="me-2"><Calendar size="20" /></span> Attiva
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Item 
                                                                        onClick={() => {
                                                                            setItemToDelete(item);
                                                                            setShowDeleteModal(true);
                                                                        }}
                                                                        disabled={loading}
                                                                        className="text-danger"
                                                                    >
                                                                        <span className="me-2"><Trash size="20" /></span> Elimina
                                                                    </Dropdown.Item>
                                                                </>
                                                            )}
                                                            {item.status === 'ACTIVE' && (
                                                                <Dropdown.Item 
                                                                    onClick={() => handleCancel(item)}
                                                                    disabled={loading}
                                                                    className="text-danger"
                                                                >
                                                                    <span className="me-2"><Trash size="20" /></span> Cancella
                                                                </Dropdown.Item>
                                                            )}
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        );
                    })()}
                </Card.Body>
            </Card>

            {/* Modal per creazione/modifica */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {errorMessage && (
                        <div className="alert alert-danger" role="alert">
                            {errorMessage}
                        </div>
                    )}
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Sport *</Form.Label>
                                    {enabledSports.length > 0 ? (
                                        <Select
                                            value={enabledSports.find(option => option.value === formData.sport)}
                                            onChange={(selectedOption) => setFormData({ ...formData, sport: selectedOption?.value || '' })}
                                            options={enabledSports}
                                            placeholder="Seleziona sport"
                                            isDisabled={editingItem && !canEdit(editingItem)}
                                        />
                                    ) : (
                                        <div className="text-muted">Caricamento sport...</div>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Data Inizio *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={formData.startDate}
                                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                        disabled={editingItem && !canEdit(editingItem)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Data Fine *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={formData.endDate}
                                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                        disabled={editingItem && !canEdit(editingItem)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <hr />
                        
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Orari Settimanali</h6>
                            {(!editingItem || canEdit(editingItem)) && (
                                <div className="d-flex gap-2">
                                    <Button variant="outline-primary" size="sm" className="btn-add" onClick={addScheduleItem}>
                                        <Add size="14" className="me-1" />
                                        Aggiungi Orario
                                    </Button>
                                    <Button variant="outline-success" size="sm" className="btn-add" onClick={generateAllDays}>
                                        <Add size="14" className="me-1" />
                                        Genera per tutti i giorni
                                    </Button>
                                </div>
                            )}
                        </div>

                        {formData.scheduleItems.map((item, index) => (
                            <Row key={item.id || index} className="mb-3 align-items-end">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Giorno</Form.Label>
                                        <Select
                                            value={dayOptions.find(option => option.value === item.dayOfWeek)}
                                            onChange={(selectedOption) => updateScheduleItem(index, 'dayOfWeek', selectedOption?.value || '')}
                                            options={dayOptions}
                                            placeholder="Seleziona giorno"
                                            isDisabled={editingItem && !canEdit(editingItem)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Ora Inizio</Form.Label>
                                        <Form.Control
                                            type="time"
                                            value={item.startTime}
                                            onChange={(e) => updateScheduleItem(index, 'startTime', e.target.value)}
                                            disabled={editingItem && !canEdit(editingItem)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Ora Fine</Form.Label>
                                        <Form.Control
                                            type="time"
                                            value={item.endTime}
                                            onChange={(e) => updateScheduleItem(index, 'endTime', e.target.value)}
                                            disabled={editingItem && !canEdit(editingItem)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label>Luogo</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={item.location || ''}
                                            onChange={(e) => updateScheduleItem(index, 'location', e.target.value)}
                                            placeholder="Opzionale"
                                            disabled={editingItem && !canEdit(editingItem)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={1}>
                                    {(!editingItem || canEdit(editingItem)) && (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={async () => { if (await chiediConferma({ titolo: 'Togliere la fascia?', testo: 'La fascia oraria sparisce dalla disponibilità.' })) removeScheduleItem(index); }}
                                        >
                                            <Trash size="14" />
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                        ))}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Annulla
                    </Button>
                    {(!editingItem || canEdit(editingItem)) && (
                        <>
                            <Button 
                                variant="primary" 
                                onClick={handleSave}
                                disabled={loading || !formData.sport || !formData.startDate || !formData.endDate}
                            >
                                {loading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Salvando...
                                    </>
                                ) : (
                                    editingItem ? 'Aggiorna' : 'Salva come bozza'
                                )}
                            </Button>
                            <Button 
                                variant="success" 
                                onClick={handleSaveAndActivate}
                                disabled={loading || !formData.sport || !formData.startDate || !formData.endDate}
                                className="ms-2"
                            >
                                {loading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Salvando e attivando...
                                    </>
                                ) : (
                                    editingItem ? 'Aggiorna e attiva' : 'Salva e attiva'
                                )}
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Modal di conferma eliminazione */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Conferma Eliminazione</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Sei sicuro di voler eliminare questa disponibilità? L'operazione non può essere annullata.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Annulla
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={loading}>
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Eliminando...
                            </>
                        ) : (
                            'Elimina'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal di conferma attivazione */}
            <Modal show={showActivateModal} onHide={() => setShowActivateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Conferma Attivazione</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Sei sicuro di voler attivare questa schedulazione? Una volta attivata, non potrà più essere modificata.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowActivateModal(false)}>
                        Annulla
                    </Button>
                    <Button variant="success" onClick={confirmActivate} disabled={loading}>
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Attivando...
                            </>
                        ) : (
                            'Attiva'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal di conferma cancellazione */}
            <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Conferma Cancellazione</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Sei sicuro di voler cancellare questa schedulazione attiva? L'operazione non può essere annullata.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
                        Annulla
                    </Button>
                    <Button variant="warning" onClick={confirmCancel} disabled={loading}>
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Cancellando...
                            </>
                        ) : (
                            'Cancella'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default DisponibilitaMaestro;
